import React, { useMemo } from 'react';
import { Text, Icon, Button } from '../ui/index.jsx';

/**
 * Get language from file extension
 */
function getLanguage(filePath) {
  const ext = filePath?.split('.').pop()?.toLowerCase();
  const langMap = {
    'js': 'javascript',
    'jsx': 'javascript',
    'ts': 'typescript',
    'tsx': 'typescript',
    'css': 'css',
    'html': 'html',
    'json': 'json',
    'md': 'markdown',
    'toml': 'toml',
    'yaml': 'yaml',
    'yml': 'yaml',
  };
  return langMap[ext] || 'text';
}

/**
 * Simple syntax highlighting (basic keywords)
 */
function highlightCode(code, language) {
  if (!code) return '';

  // Ensure code is a string (handle objects, arrays, streaming chunks, etc.)
  const codeStr = typeof code === 'string' ? code : JSON.stringify(code, null, 2);

  // Escape HTML
  let highlighted = codeStr
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Apply basic highlighting based on language
  if (['javascript', 'typescript'].includes(language)) {
    // Keywords
    highlighted = highlighted.replace(
      /\b(const|let|var|function|return|if|else|for|while|import|export|from|default|class|extends|async|await|try|catch|throw|new|this|typeof|instanceof)\b/g,
      '<span class="text-purple-400">$1</span>'
    );
    // Strings
    highlighted = highlighted.replace(
      /(["'`])(?:(?!\1)[^\\]|\\.)*?\1/g,
      '<span class="text-green-400">$&</span>'
    );
    // Comments
    highlighted = highlighted.replace(
      /(\/\/.*$)/gm,
      '<span class="text-gray-500">$1</span>'
    );
    highlighted = highlighted.replace(
      /(\/\*[\s\S]*?\*\/)/g,
      '<span class="text-gray-500">$1</span>'
    );
    // Numbers
    highlighted = highlighted.replace(
      /\b(\d+\.?\d*)\b/g,
      '<span class="text-orange-400">$1</span>'
    );
    // JSX tags
    highlighted = highlighted.replace(
      /(&lt;\/?[A-Z][a-zA-Z0-9]*)/g,
      '<span class="text-blue-400">$1</span>'
    );
  } else if (language === 'css') {
    // Selectors
    highlighted = highlighted.replace(
      /([.#]?[a-zA-Z_-][a-zA-Z0-9_-]*)\s*\{/g,
      '<span class="text-yellow-400">$1</span> {'
    );
    // Properties
    highlighted = highlighted.replace(
      /([a-z-]+)\s*:/g,
      '<span class="text-blue-400">$1</span>:'
    );
    // Values
    highlighted = highlighted.replace(
      /:\s*([^;]+);/g,
      ': <span class="text-green-400">$1</span>;'
    );
  } else if (language === 'json') {
    // Keys
    highlighted = highlighted.replace(
      /"([^"]+)":/g,
      '<span class="text-blue-400">"$1"</span>:'
    );
    // String values
    highlighted = highlighted.replace(
      /:\s*"([^"]+)"/g,
      ': <span class="text-green-400">"$1"</span>'
    );
    // Numbers and booleans
    highlighted = highlighted.replace(
      /:\s*(true|false|null|\d+)/g,
      ': <span class="text-orange-400">$1</span>'
    );
  }

  return highlighted;
}

/**
 * FilePreviewPanel - displays file content with syntax highlighting
 */
export default function FilePreviewPanel({
  filePath,
  content,
  onClose,
  onCopy,
  className = ''
}) {
  // Ensure content is a string for all operations
  const contentStr = useMemo(() => {
    if (!content) return '';
    return typeof content === 'string' ? content : JSON.stringify(content, null, 2);
  }, [content]);

  const language = useMemo(() => getLanguage(filePath), [filePath]);
  const highlightedContent = useMemo(() => highlightCode(contentStr, language), [contentStr, language]);
  const lineCount = contentStr.split('\n').length;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(contentStr);
      onCopy?.();
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!filePath) {
    return (
      <div className={`flex flex-col items-center justify-center h-full text-gray-500 bg-[#0D0D10] ${className}`}>
        <Icon name="file-code" size="lg" className="mb-2 opacity-50" />
        <Text className="text-sm">Select a file to preview</Text>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full bg-[#0D0D10] ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#2A2A3C] bg-[#1A1A24]">
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="file-code" size="sm" className="text-[#7B61FF] flex-shrink-0" />
          <Text className="text-sm font-mono text-gray-300 truncate">{filePath}</Text>
          <Text className="text-xs text-gray-500 flex-shrink-0">({lineCount} lines)</Text>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="xs"
            onClick={handleCopy}
            className="text-gray-400 hover:text-white"
          >
            <Icon name="copy" size="xs" className="mr-1" />
            Copy
          </Button>
          {onClose && (
            <Button
              variant="ghost"
              size="xs"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <Icon name="x" size="xs" />
            </Button>
          )}
        </div>
      </div>

      {/* Code content */}
      <div className="flex-1 overflow-auto">
        <div className="flex min-w-full">
          {/* Line numbers */}
          <div className="flex-shrink-0 px-3 py-2 text-right text-xs font-mono text-gray-600 bg-[#0D0D10] select-none border-r border-[#2A2A3C]">
            {contentStr.split('\n').map((_, i) => (
              <div key={i} className="leading-5">{i + 1}</div>
            ))}
          </div>
          {/* Code */}
          <pre className="flex-1 px-4 py-2 text-sm font-mono text-gray-300 overflow-x-auto">
            <code
              dangerouslySetInnerHTML={{ __html: highlightedContent }}
              className="leading-5 block"
            />
          </pre>
        </div>
      </div>
    </div>
  );
}
